// Walks the sparse, variable-depth quadtree produced by
// adaptive_mesh_tile_chunker.py. Leaves live at whatever zoom the chunker
// stopped splitting at, so a normal deck.gl TileLayer (which requests a
// single uniform zoom per viewport) doesn't apply -- instead we descend
// from manifest.root_zoom ourselves.
//
// When the cycle has GMSH/grid overview tiles (manifest.has_overviews),
// the walk can stop at a parent overview whose z meets the viewport LOD
// target instead of always descending to leaves. Overview bins use the
// same z/x/y/geometry.bin + scalar_tN.bin layout as leaves.
//
// Culling uses each leaf's REAL data bbox (from meta.json, folded into
// manifest.json by rebuild_manifest_with_bbox.py), not the nominal
// Web-Mercator grid cell. Internal nodes carry the union of descendant
// leaf bboxes so entire subtrees can be skipped when nothing below could
// face the camera.
//
// Globe culling is two-stage:
//   1. Geographic screen footprint (tight when zoomed in — Florida Keys
//      should be a few degrees, not a hemisphere).
//   2. Spherical dot cone (drops back-side tiles; used alone when the
//      screen footprint is degenerate near poles / antimeridian).

/** Overviews only while camera zoom is at or below this (else leaf tiles). */
export const OVERVIEW_MAX_VIEWPORT_ZOOM = 5.0;

function unionBBox(a, b) {
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3])
  ];
}

export function buildQuadtreeIndex(manifest) {
  const leafSet = new Set();
  const leafBBox = new Map(); // "z,x,y" -> [lonMin, latMin, lonMax, latMax]
  const internalSet = new Set();
  const internalBBox = new Map();
  const overviewSet = new Set();
  const overviewBBox = new Map();
  const rootZoom = manifest.root_zoom;

  for (const [z, x, y, lonMin, latMin, lonMax, latMax] of manifest.tiles) {
    const key = `${z},${x},${y}`;
    leafSet.add(key);
    const bbox = [lonMin, latMin, lonMax, latMax];
    leafBBox.set(key, bbox);
    let cz = z;
    let cx = x;
    let cy = y;
    while (cz > rootZoom) {
      cz -= 1;
      cx = cx >> 1;
      cy = cy >> 1;
      const parentKey = `${cz},${cx},${cy}`;
      internalSet.add(parentKey);
      const prev = internalBBox.get(parentKey);
      internalBBox.set(parentKey, prev ? unionBBox(prev, bbox) : bbox.slice());
    }
  }

  // Prefer a non-empty overviews[] even if has_overviews was omitted.
  const hasOverviews = Array.isArray(manifest.overviews) && manifest.overviews.length > 0;
  if (hasOverviews) {
    for (const row of manifest.overviews) {
      const z = row[0];
      const x = row[1];
      const y = row[2];
      const key = `${z},${x},${y}`;
      overviewSet.add(key);
      // Remesh bbox is wet-only and can miss dry interior of a parent cell.
      // For LOD / culling, expand to the descendant-leaf union so looking at
      // continental US still selects the overview instead of pruning the tree.
      let bbox = null;
      if (row.length >= 7) {
        bbox = [row[3], row[4], row[5], row[6]];
      }
      const internal = internalBBox.get(key);
      if (internal) {
        bbox = bbox ? unionBBox(bbox, internal) : internal.slice();
      }
      if (bbox) overviewBBox.set(key, bbox);
    }
  }

  let overviewTimesteps = null;
  if (hasOverviews && Array.isArray(manifest.overview_timesteps)) {
    overviewTimesteps = new Set(manifest.overview_timesteps.map(Number));
  }
  const overviewHasVectors = Boolean(manifest.overview_has_vectors);
  const overviewMaxZoom =
    manifest.overview_max_zoom != null ? Number(manifest.overview_max_zoom) : null;
  const overviewLodBias =
    manifest.overview_lod_bias != null ? Number(manifest.overview_lod_bias) : 1.35;
  const overviewMaxViewportZoom =
    manifest.overview_max_viewport_zoom != null
      ? Number(manifest.overview_max_viewport_zoom)
      : OVERVIEW_MAX_VIEWPORT_ZOOM;

  const rootTiles = [];
  const n = 2 ** rootZoom;
  for (let x = 0; x < n; x++) {
    for (let y = 0; y < n; y++) {
      const key = `${rootZoom},${x},${y}`;
      if (leafSet.has(key) || internalSet.has(key)) {
        rootTiles.push([rootZoom, x, y]);
      }
    }
  }

  return {
    leafSet,
    leafBBox,
    internalSet,
    internalBBox,
    overviewSet,
    overviewBBox,
    hasOverviews,
    overviewTimesteps,
    overviewHasVectors,
    overviewMaxZoom,
    overviewLodBias,
    overviewMaxViewportZoom,
    rootZoom,
    rootTiles
  };
}

/** Best known data bbox for a tile key (leaf, overview, or internal union). */
export function tileBBoxForKey(index, key) {
  return (
    index.leafBBox.get(key) ||
    index.overviewBBox?.get(key) ||
    index.internalBBox.get(key) ||
    null
  );
}

function tileYToLat(y, n) {
  const t = Math.PI * (1 - (2 * y) / n);
  return (180 / Math.PI) * Math.atan(Math.sinh(t));
}

export function tileToBBox(z, x, y) {
  const n = 2 ** z;
  const lonLeft = (x / n) * 360 - 180;
  const lonRight = ((x + 1) / n) * 360 - 180;
  const latTop = tileYToLat(y, n);
  const latBottom = tileYToLat(y + 1, n);
  return [lonLeft, latBottom, lonRight, latTop];
}

export function bboxIntersects(a, b) {
  return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
}

// Tile bboxes near ±180 are stored as signed lon min/max. A tile with verts
// only on the -179 side will not intersect a screen bbox unwrapped near
// +179 under a naive AABB (rectangular holes by the dateline) — solved by
// testing ±360 shifts in lonIntervalOverlaps.
//
// Lon span > 180° is ambiguous: either a single antimeridian leaf (min/max
// of mixed-sign verts) or — much more often — an internal-node union of
// children on both sides of the Pacific. Treating that as the thin strips
// at ±180 false-culls Hawaii / eastern Australia when zoomed in (screen
// bbox is tight; the broken parent fails intersection and the whole
// subtree is skipped). Prefer no false negatives: cover all longitudes;
// lat + sphere cull still drop the wrong hemisphere.
function bboxLonIntervals(lonMin, lonMax) {
  if (lonMax - lonMin > 180) {
    return [[-180, 180]];
  }
  return [[lonMin, lonMax]];
}

function lonIntervalOverlaps(a0, a1, b0, b1) {
  for (const shift of [0, 360, -360]) {
    const c0 = b0 + shift;
    const c1 = b1 + shift;
    if (a0 < c1 && a1 > c0) return true;
  }
  return false;
}

export function bboxIntersectsAntimeridianAware(a, b) {
  if (!(a[1] < b[3] && a[3] > b[1])) return false;
  const aLons = bboxLonIntervals(a[0], a[2]);
  const bLons = bboxLonIntervals(b[0], b[2]);
  for (const [a0, a1] of aLons) {
    for (const [b0, b1] of bLons) {
      if (lonIntervalOverlaps(a0, a1, b0, b1)) return true;
    }
  }
  return false;
}

function bboxSpanOk(bbox) {
  if (!bbox) return false;
  const lonSpan = bbox[2] - bbox[0];
  const latSpan = bbox[3] - bbox[1];
  return lonSpan <= 120 && latSpan <= 80 && lonSpan >= 0 && latSpan >= 0;
}

function viewportScreenReady(viewport) {
  const w = viewport?.width ?? 0;
  const h = viewport?.height ?? 0;
  return w >= 32 && h >= 32;
}

function readViewportBoundsFallback(viewport) {
  try {
    const bounds = viewport.getBounds();
    return bboxSpanOk(bounds) ? bounds : null;
  } catch {
    return null;
  }
}

// Rough lower bound on how wide the screen footprint should be at a given zoom.
function minExpectedLonSpan(zoom) {
  const z = Math.max(0, Number(zoom) || 0);
  return 360 / Math.pow(2, z + 0.85);
}

export function lonLatToUnitVector(lon, lat) {
  const lonRad = (lon * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);
  return [cosLat * Math.cos(lonRad), cosLat * Math.sin(lonRad), Math.sin(latRad)];
}

export function dot3(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

const GLOBE_MIN_DOT_FALLBACK = -0.6;

export function zoomBasedMinDot(zoom) {
  const z = Math.max(0, Number(zoom) || 0);
  return Math.min(0.92, Math.max(0, (z - 0.5) * 0.095));
}

function wrapLonNear(lon, refLon) {
  let l = lon;
  while (l - refLon > 180) l -= 360;
  while (l - refLon < -180) l += 360;
  return l;
}

function sampleScreenGlobeHits(viewport, grid = 7) {
  const cameraDir = lonLatToUnitVector(viewport.longitude, viewport.latitude);
  const {width, height, longitude: camLon} = viewport;
  const hits = [];

  for (let gy = 0; gy < grid; gy++) {
    for (let gx = 0; gx < grid; gx++) {
      const px = (gx / (grid - 1)) * width;
      const py = (gy / (grid - 1)) * height;
      const hit = viewport.unproject([px, py]);
      if (!hit || hit.length < 2) continue;
      const lon = wrapLonNear(hit[0], camLon);
      const lat = hit[1];
      const dot = dot3(lonLatToUnitVector(lon, lat), cameraDir);
      if (dot <= 0.05) continue;
      hits.push({lon, lat, dot});
    }
  }
  return hits;
}

export function computeGlobeCullMinDotDetails(viewport) {
  const zoomFloor = zoomBasedMinDot(viewport.zoom);
  const hits = sampleScreenGlobeHits(viewport, 5);

  let minDot = 1;
  for (const h of hits) {
    minDot = Math.min(minDot, h.dot);
  }

  if (!hits.length) {
    return {
      minDot: zoomFloor,
      zoomFloor,
      fromScreen: null,
      screenSamples: 0,
      zoom: viewport.zoom
    };
  }

  const fromScreen = minDot - 0.03;
  return {
    minDot: Math.max(zoomFloor, fromScreen),
    zoomFloor,
    fromScreen,
    screenSamples: hits.length,
    zoom: viewport.zoom
  };
}

export function computeGlobeCullMinDot(viewport) {
  return computeGlobeCullMinDotDetails(viewport).minDot;
}

// Geographic lon/lat bounds of what is on screen (front hemisphere only).
// Returns null when the footprint is too large to trust (poles / world view).
export function computeGlobeScreenBBox(viewport) {
  if (!viewport || typeof viewport.unproject !== 'function') return null;
  if (!viewportScreenReady(viewport)) {
    return readViewportBoundsFallback(viewport);
  }

  const hits = sampleScreenGlobeHits(viewport, 7);
  if (hits.length < 4) {
    return readViewportBoundsFallback(viewport);
  }

  let lonMin = Infinity;
  let lonMax = -Infinity;
  let latMin = Infinity;
  let latMax = -Infinity;
  for (const h of hits) {
    lonMin = Math.min(lonMin, h.lon);
    lonMax = Math.max(lonMax, h.lon);
    latMin = Math.min(latMin, h.lat);
    latMax = Math.max(latMax, h.lat);
  }

  const lonSpan = lonMax - lonMin;
  const latSpan = latMax - latMin;
  if (!bboxSpanOk([lonMin, latMin, lonMax, latMax])) {
    return null;
  }

  // Before layout settles, every screen sample can collapse to the center pixel
  // and produce a tiny bbox that hides most tiles until the user pans.
  const minLon = minExpectedLonSpan(viewport.zoom);
  if (lonSpan < minLon * 0.35 || latSpan < minLon * 0.22) {
    return readViewportBoundsFallback(viewport);
  }

  const marginLon = Math.max(0.2, lonSpan * 0.06);
  const marginLat = Math.max(0.2, latSpan * 0.06);
  return [lonMin - marginLon, latMin - marginLat, lonMax + marginLon, latMax + marginLat];
}

function bboxGlobeVisible(bbox, cameraDir, minDot) {
  const [lonMin, latMin, lonMax, latMax] = bbox;
  const lonMid = (lonMin + lonMax) / 2;
  const latMid = (latMin + latMax) / 2;
  const samples = [
    [lonMin, latMin],
    [lonMax, latMin],
    [lonMin, latMax],
    [lonMax, latMax]
  ];
  if (lonMax - lonMin > 180) {
    // Wrapped dateline tile: geographic mid is meaningless (often near 0°).
    samples.push([180, latMid], [-180, latMid], [lonMin, latMid], [lonMax, latMid]);
  } else {
    samples.push([lonMid, latMid]);
  }

  let best = -Infinity;
  for (const [lon, lat] of samples) {
    best = Math.max(best, dot3(lonLatToUnitVector(lon, lat), cameraDir));
  }
  return best > minDot;
}

function canStopAtOverview(index, key, z, lod = {}) {
  if (!index.hasOverviews || !index.overviewSet?.has(key)) return false;
  if (lod.forceLeaves) return false;
  if (
    index.overviewTimesteps &&
    lod.timestep != null &&
    !index.overviewTimesteps.has(Number(lod.timestep))
  ) {
    return false;
  }
  const viewportZoom = Number(lod.viewportZoom);
  if (!Number.isFinite(viewportZoom)) return false;

  // Hard cutoff: overs for zoomed-out views only; zoom > 5 → leaves.
  const maxViewZoom = Number.isFinite(index.overviewMaxViewportZoom)
    ? index.overviewMaxViewportZoom
    : OVERVIEW_MAX_VIEWPORT_ZOOM;
  if (viewportZoom > maxViewZoom) return false;

  // Within the cutoff, stop at an overview fine enough for the camera.
  const desiredZ = Math.max(index.rootZoom, Math.floor(viewportZoom + 1e-6));
  if (
    index.overviewMaxZoom != null &&
    z < index.overviewMaxZoom &&
    z < desiredZ
  ) {
    return false;
  }
  return true;
}

function walkVisibleTiles(index, isVisible, lod = {}) {
  const out = [];

  function visit(z, x, y) {
    const key = `${z},${x},${y}`;
    if (index.leafSet.has(key)) {
      if (isVisible(index.leafBBox.get(key))) {
        out.push([z, x, y]);
      }
      return;
    }
    if (!index.internalSet.has(key)) {
      return;
    }
    if (!isVisible(index.internalBBox?.get(key))) {
      return;
    }

    if (canStopAtOverview(index, key, z, lod)) {
      const bbox = index.overviewBBox.get(key) || index.internalBBox.get(key);
      if (isVisible(bbox)) {
        out.push([z, x, y]);
        return;
      }
      // Overview present but its bbox missed this view while descendants may
      // still be visible — fall through and descend instead of blanking.
    }

    const cz = z + 1;
    for (const [dx, dy] of [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1]
    ]) {
      visit(cz, 2 * x + dx, 2 * y + dy);
    }
  }

  for (const [z, x, y] of index.rootTiles) {
    visit(z, x, y);
  }
  return out;
}

export function collectVisibleLeaves(index, viewportBBox, lod = {}) {
  return walkVisibleTiles(
    index,
    bbox => bbox && bboxIntersectsAntimeridianAware(bbox, viewportBBox),
    lod
  );
}

export function collectVisibleLeavesGlobe(
  index,
  cameraLon,
  cameraLat,
  minDot = GLOBE_MIN_DOT_FALLBACK,
  screenBBox = null,
  lod = {}
) {
  const cameraDir = lonLatToUnitVector(cameraLon, cameraLat);
  const useScreenBBox = screenBBox && bboxSpanOk(screenBBox);
  // Geographic screen bbox already limits to what's in view; dot test
  // then only needs to drop the back side of the globe, not apply a second
  // tight cone (high minDot at zoom-in was zeroing visible tiles).
  const dotThreshold = useScreenBBox ? Math.min(minDot, 0.02) : minDot;

  return walkVisibleTiles(
    index,
    bbox => {
      if (!bbox) return false;
      if (useScreenBBox && !bboxIntersectsAntimeridianAware(bbox, screenBBox)) {
        return false;
      }
      return bboxGlobeVisible(bbox, cameraDir, dotThreshold);
    },
    lod
  );
}
